import React from 'react';
import { Play, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';
import { trackVideoClick } from '../utils/tracking';

/**
 * VideoCard - Displays a video thumbnail that links to YouTube
 */
export const VideoCard = ({ 
  video, 
  size = 'default', // 'small', 'default', 'large'
  showDescription = true,
  showProductTag = true 
}) => {
  const {
    title,
    thumbnail_url,
    video_url,
    description,
    related_product,
    badge
  } = video;

  const handleClick = () => {
    trackVideoClick(video);
    window.open(video_url, '_blank', 'noopener,noreferrer');
  };

  const productLabels = {
    'herb-savory': { label: 'Herb & Savory', color: '#2D5016' },
    'citrus-garlic': { label: 'Citrus Garlic', color: '#D4540A' },
    'smoky-savory': { label: 'Smoky Savory', color: '#5C3D2E' },
    'seafood-seasoning': { label: 'Seafood Seasoning', color: '#1E4D6B' },
    'full-set': { label: 'Full Set', color: '#2A2A2A' },
    'general': { label: 'All Seasonings', color: '#666666' }
  };

  const productInfo = productLabels[related_product] || productLabels['general'];

  const badgeConfig = {
    most_popular: { label: 'Most Popular', bg: 'bg-amber-600', icon: '★' },
    staff_pick: { label: 'Staff Pick', bg: 'bg-emerald-700', icon: '✦' },
  };
  const activeBadge = badge && badge !== 'none' ? badgeConfig[badge] : null;

  const sizeClasses = {
    small: 'max-w-[280px]',
    default: 'max-w-[360px]',
    large: 'max-w-[480px]'
  };

  return (
    <div 
      className={`group bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 border border-[#E5E0D8] cursor-pointer ${sizeClasses[size]}`}
      onClick={handleClick}
      data-testid={`video-card-${video.id}`}
    >
      {/* Thumbnail with Play Overlay */}
      <div className="relative aspect-video overflow-hidden bg-[#2A2A2A]">
        <img
          src={thumbnail_url}
          alt={title}
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          loading="lazy"
        />
        
        {/* Play Button Overlay */}
        <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/40 transition-colors">
          <div className="w-16 h-16 rounded-full bg-white/90 group-hover:bg-white flex items-center justify-center shadow-lg transition-all group-hover:scale-110">
            <Play className="w-7 h-7 text-[#D4540A] ml-1" fill="#D4540A" />
          </div>
        </div>

        {/* Product Tag */}
        {showProductTag && (
          <div 
            className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-xs font-bold text-white shadow"
            style={{ backgroundColor: productInfo.color }}
          >
            {productInfo.label}
          </div>
        )}

        {/* Badge */}
        {activeBadge && (
          <div 
            className={`absolute top-3 right-3 ${activeBadge.bg} text-white px-2.5 py-1 rounded-full text-xs font-bold shadow-md flex items-center gap-1`}
            data-testid={`video-badge-${badge}`}
          >
            <span className="text-[10px]">{activeBadge.icon}</span>
            {activeBadge.label}
          </div>
        )}

        {/* YouTube Badge */}
        <div className="absolute bottom-3 right-3 bg-red-600 text-white px-2 py-0.5 rounded text-xs font-bold flex items-center gap-1">
          <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
            <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
          </svg>
          YouTube
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="font-semibold text-[#2A2A2A] line-clamp-2 group-hover:text-[#D4540A] transition-colors">
          {title}
        </h3>
        
        {showDescription && description && (
          <p className="text-sm text-[#666] mt-2 line-clamp-2">
            {description}
          </p>
        )}

        <Button
          variant="ghost"
          className="mt-3 w-full text-[#D4540A] hover:text-[#B84709] hover:bg-[#FFF5F0] font-medium"
          onClick={(e) => {
            e.stopPropagation();
            handleClick();
          }}
        >
          Watch Full Video
          <ExternalLink className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
};

/**
 * VideoCardCompact - Smaller video card for product modals
 */
export const VideoCardCompact = ({ video }) => {
  const handleClick = () => {
    trackVideoClick(video);
    window.open(video.video_url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div 
      className="group flex gap-3 p-2 rounded-lg hover:bg-[#F5F3F0] cursor-pointer transition-colors"
      onClick={handleClick}
      data-testid={`video-compact-${video.id}`}
    >
      {/* Thumbnail */}
      <div className="relative w-28 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-[#2A2A2A]">
        <img
          src={video.thumbnail_url}
          alt={video.title}
          className="w-full h-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/40 transition-colors">
          <div className="w-8 h-8 rounded-full bg-white/90 flex items-center justify-center">
            <Play className="w-4 h-4 text-[#D4540A] ml-0.5" fill="#D4540A" />
          </div>
        </div>
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <h4 className="text-sm font-medium text-[#2A2A2A] line-clamp-2 group-hover:text-[#D4540A] transition-colors">
          {video.title}
        </h4>
        <p className="text-xs text-[#888] mt-1 flex items-center gap-1">
          <ExternalLink className="w-3 h-3" />
          Watch on YouTube
        </p>
      </div>
    </div>
  );
};

export default VideoCard;
